import React from "react";
import { Modal } from "react-bootstrap";

interface KeyboardShortcutsModalProps {
  show: boolean;
  onHide: () => void;
}

interface ShortcutItem {
  keys: string;
  description: string;
}

interface ShortcutGroup {
  title: string;
  shortcuts: ShortcutItem[];
}

const isMac =
  typeof navigator !== "undefined" &&
  /Mac|iPod|iPhone|iPad/.test(navigator.platform);

const mod = isMac ? "\u2318" : "Ctrl";
const shift = isMac ? "\u21E7" : "Shift";

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: "File",
    shortcuts: [
      { keys: `${mod}+N`, description: "New document" },
      { keys: `${mod}+S`, description: "Save" },
      { keys: `${mod}+P`, description: "Print" },
    ],
  },
  {
    title: "Edit",
    shortcuts: [
      { keys: `${mod}+Z`, description: "Undo" },
      { keys: `${mod}+Y`, description: "Redo" },
      { keys: `${mod}+X`, description: "Cut" },
      { keys: `${mod}+C`, description: "Copy" },
      { keys: `${mod}+V`, description: "Paste" },
      { keys: `${mod}+${shift}+V`, description: "Paste without formatting" },
      { keys: `${mod}+A`, description: "Select all" },
      { keys: `${mod}+F`, description: "Find" },
      { keys: `${mod}+H`, description: "Find & Replace" },
    ],
  },
  {
    title: "Formatting",
    shortcuts: [
      { keys: `${mod}+B`, description: "Bold" },
      { keys: `${mod}+I`, description: "Italic" },
      { keys: `${mod}+U`, description: "Underline" },
      { keys: `${mod}+K`, description: "Insert link" },
      { keys: `${mod}+Alt+F`, description: "Insert footnote" },
    ],
  },
  {
    title: "Navigation & View",
    shortcuts: [
      { keys: `Tab`, description: "Increase indent" },
      { keys: `${shift}+Tab`, description: "Decrease indent" },
      { keys: `${mod}+=`, description: "Zoom in" },
      { keys: `${mod}+-`, description: "Zoom out" },
      { keys: `${mod}+0`, description: "Reset zoom" },
    ],
  },
  {
    title: "Find & Replace",
    shortcuts: [
      { keys: `Enter`, description: "Next match" },
      { keys: `${shift}+Enter`, description: "Previous match" },
      { keys: `Escape`, description: "Close find bar" },
    ],
  },
];

const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({
  show,
  onHide,
}) => {
  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "18px" }}>
          Keyboard Shortcuts
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <div className="keyboard-shortcuts-grid">
          {SHORTCUT_GROUPS.map(group => (
            <div key={group.title} className="keyboard-shortcuts-group">
              <h6 className="keyboard-shortcuts-group-title">{group.title}</h6>
              <table className="keyboard-shortcuts-table">
                <tbody>
                  {group.shortcuts.map(shortcut => (
                    <tr key={shortcut.keys}>
                      <td className="keyboard-shortcuts-keys">
                        {shortcut.keys.split("+").map((key, i) => (
                          <React.Fragment key={i}>
                            {i > 0 && (
                              <span className="keyboard-shortcuts-plus">+</span>
                            )}
                            <kbd className="keyboard-shortcuts-key">{key}</kbd>
                          </React.Fragment>
                        ))}
                      </td>
                      <td className="keyboard-shortcuts-desc">
                        {shortcut.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default KeyboardShortcutsModal;
